/**
 * PlaybookTab — the internal source of truth for how Wildy works and how we answer operators.
 * The public /for-business page states the same facts for operators; keep the two in step.
 * Reply rules: say "at this stage" for anything about the model, and add the long-term vision line with 😊.
 */

import { useMemo, useState } from 'react';
import { Check, ChevronDown, Copy, ExternalLink, Minus, Search, Telescope } from 'lucide-react';

const PUBLIC_PAGE = 'https://wildy.world/for-business';

type Lang = 'en' | 'es';
type Snippet = Record<Lang, string>;

const PITCH: Snippet = {
  en: 'Wildy is a map of small, nature-first experiences. At this stage we list your tours for free and send interested travellers straight to your own website to book.',
  es: 'Wildy es un mapa de experiencias pequeñas y de naturaleza. En esta etapa publicamos tus actividades gratis y enviamos a los viajeros interesados directamente a tu web para reservar.',
};

const TODAY = [
  'Listing is free: no fee, no commission, no contract',
  'We build the page from their website and keep it up to date',
  'The Book button opens their own booking page, tagged ?ref=wildy',
  'They can ask for a login with Partner Stats (work in progress): views, map clicks, website clicks',
  'Changes or removal: one email to info@wildy.world',
];
const NOT_YET = [
  'No bookings or payments on Wildy (every business is in redirect mode)',
  'No fees or commission of any kind',
  'No API or booking-system integration (Bókun, FareHarbor…); we just link',
  'Operators don\'t need to edit anything themselves',
];
const LATER = [
  'Once Wildy brings them real traffic, a reasonable fee, enough to pay for our work. Never before that',
  'We tell partners first, and they can leave whenever they like',
  'Vision: offer operators all the tech they need (bookings, payments, dashboard) in one place 😊',
];

const ANSWERS: { q: string; who: string; a: Snippet }[] = [
  {
    q: 'Are you an OTA?',
    who: 'Northern Epics',
    a: {
      en: "No, we're not an OTA. We don't resell your tours, set prices or take payments. Wildy is a place for travellers to discover you, and at this stage the booking always happens on your own website.",
      es: 'No, no somos una OTA. No revendemos tus actividades, no fijamos precios ni cobramos pagos. Wildy es un sitio donde los viajeros te descubren, y en esta etapa la reserva siempre se hace en tu propia web.',
    },
  },
  {
    q: 'How do you make money? / What\'s the catch? / What\'s in it for you?',
    who: 'Northern Epics, North Wind Lapland, Hiking Faroe, Into Nature',
    a: {
      en: "Fair question, no catch! At this stage we don't make any money: we're self-funding this phase and focused on building the audience first. If we get to the point of bringing you real traffic, we'll charge a reasonable fee, enough to pay for our work, so we only earn once we're useful to you. Long term, we'd love to be able to offer operators like you all the tech you need, from bookings to payments, in one place 😊",
      es: '¡Buena pregunta, no hay truco! En esta etapa no ganamos dinero: nos autofinanciamos y nos centramos primero en construir la audiencia. Si llegamos a traerte tráfico real, cobraremos una cuota razonable, suficiente para pagar nuestro trabajo, así que solo ganaremos cuando te seamos útiles. A largo plazo, nos encantaría poder ofrecer a operadores como tú toda la tecnología que necesitéis, de las reservas a los pagos, en un solo sitio 😊',
    },
  },
  {
    q: 'Is there a commission?',
    who: 'MOLARI, Wild About Lapland',
    a: {
      en: "Not at this stage: no fees and no commission. If we get to the point of bringing you real traffic, we'll charge a reasonable fee, enough to pay for our work. You'd hear it from us first, and you can leave whenever you like.",
      es: 'No en esta etapa: ni cuotas ni comisión. Si llegamos a traerte tráfico real, cobraremos una cuota razonable, suficiente para pagar nuestro trabajo. Te lo contaríamos nosotros primero, y puedes irte cuando quieras.',
    },
  },
  {
    q: 'Can you connect to Bókun / FareHarbor / our API?',
    who: 'MOLARI, Wild About Lapland',
    a: {
      en: "Right now there's nothing to connect: we link straight to the booking page you already use, so your availability and prices stay in your own system. Integrations are something we'd love to offer later 😊",
      es: 'Ahora mismo no hay nada que conectar: enlazamos directamente a la página de reservas que ya usas, así que tu disponibilidad y tus precios siguen en tu propio sistema. Las integraciones son algo que nos encantaría ofrecer más adelante 😊',
    },
  },
  {
    q: 'What are the next steps? / What does a partnership look like?',
    who: 'Sail Maui',
    a: {
      en: "There's nothing you need to do. We build your page from your website (tours, prices and photos) and send you the link to check. If you'd like anything changed, just reply. At this stage the partnership is simply that: a free page that sends travellers to your own booking page.",
      es: 'No tienes que hacer nada. Creamos tu página a partir de tu web (actividades, precios y fotos) y te enviamos el enlace para que la revises. Si quieres cambiar algo, responde a este email. En esta etapa la colaboración es solo eso: una página gratis que envía viajeros a tu propia página de reservas.',
    },
  },
  {
    q: 'Who manages the listing?',
    who: 'MOLARI',
    a: {
      en: 'We do. We keep your page up to date from your website, and you can email us any change: a new tour, new prices or different photos.',
      es: 'Nosotros. Mantenemos tu página al día a partir de tu web, y puedes escribirnos cualquier cambio: una actividad nueva, precios nuevos u otras fotos.',
    },
  },
  {
    q: 'Can we see results?',
    who: 'Anyone who asks about value',
    a: {
      en: "Yes. Every link to your site carries ?ref=wildy, so you'll see us in Google Analytics or your booking tool. We're also building a login that shows your page's views and clicks; it's still a work in progress, but we can give you access.",
      es: 'Sí. Cada enlace a tu web lleva ?ref=wildy, así que nos verás en Google Analytics o en tu sistema de reservas. También estamos preparando un acceso con las visitas y clics de tu página; aún está en desarrollo, pero podemos darte acceso.',
    },
  },
  {
    q: 'Can we have a call?',
    who: 'Florida Blue Adventures',
    a: {
      en: "Of course! Would [day] at [time] or [day] at [time] (your time) work for a quick 15-minute call? Happy to adapt to whatever suits you.",
      es: '¡Claro! ¿Te vendría bien el [día] a las [hora] o el [día] a las [hora] (tu hora) para una llamada rápida de 15 minutos? Nos adaptamos a lo que mejor te venga.',
    },
  },
  {
    q: 'We\'re an NGO / can it be free?',
    who: 'Mingan Island Cetacean Study',
    a: {
      en: "Yes, it's free for everyone at this stage, and we'd love to have you. We'll build your page from your website and send you the link.",
      es: 'Sí, en esta etapa es gratis para todos, y nos encantaría teneros. Creamos vuestra página a partir de vuestra web y os enviamos el enlace.',
    },
  },
];

const LIVE_EMAIL: Snippet = {
  en: "Hi [name],\n\nYou're live on Wildy! Here's your page: [link]\n\nWe built it from your website: your tours, prices and photos. The booking button sends travellers straight to your own booking page, tagged ?ref=wildy so you can see them in your analytics.\n\nIf anything needs changing, just reply to this email.\n\nThanks for joining us at this early stage 😊\n\nMario & Guillermo\nWildy",
  es: '¡Hola [nombre]!\n\n¡Ya estás en Wildy! Esta es tu página: [enlace]\n\nLa hemos creado a partir de tu web: tus actividades, precios y fotos. El botón de reserva envía a los viajeros directamente a tu página de reservas, marcados con ?ref=wildy para que los veas en tu analítica.\n\nSi hay que cambiar algo, responde a este email.\n\nGracias por uniros en esta etapa tan temprana 😊\n\nMario y Guillermo\nWildy',
};

const RULES = [
  'Say "at this stage" or "for now" for anything about fees, commission, bookings or integrations. Never state the model as permanent.',
  'When the model comes up, add the long-term vision line ("all the tech you need") with 😊.',
  'Link to wildy.world/for-business when someone wants the full picture.',
  'Never promise a date, a feature, a traffic number or a reply time.',
  'Sign as "Mario & Guillermo" and answer in the language they wrote in.',
];

const FLOW = [
  { step: 'They say yes', how: 'A reply (Inbox tab: "Add to Wildy") or an email to info@wildy.world with the subject "Join Wildy"' },
  { step: 'Build the listing', how: '/add-customer (from their website)' },
  { step: 'Add real photos', how: '/business-images' },
  { step: 'Go live', how: 'npm run add-customer -- scripts/<slug>.json --env prod' },
  { step: 'Tell them', how: 'Send the "You\'re live" email below with their page link' },
  { step: 'Close it', how: 'Mark the task done in the Inbox tab' },
];

// ─── UI pieces ────────────────────────────────────────────────────────────────

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section className="bg-white rounded-xl border border-slate-200/60 shadow-md shadow-slate-900/5">
      <div className="px-6 pt-5 pb-3">
        <h2 className="text-base font-bold text-slate-800">{title}</h2>
        {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="px-6 pb-6">{children}</div>
    </section>
  );
}

function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [state, setState] = useState<'idle' | 'done' | 'failed'>('idle');
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setState('done'); } catch { setState('failed'); }
    setTimeout(() => setState('idle'), 1500);
  };
  return (
    <button type="button" onClick={copy} aria-live="polite"
      className={`inline-flex items-center gap-1.5 self-start rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        state === 'done' ? 'border-primary bg-brand-50 text-primary' : 'border-slate-200 bg-white text-slate-600 hover:border-primary hover:text-primary'}`}>
      {state === 'done' ? <Check className="w-3.5 h-3.5" aria-hidden /> : <Copy className="w-3.5 h-3.5" aria-hidden />}
      {state === 'done' ? 'Copied' : state === 'failed' ? 'Select & copy' : label}
    </button>
  );
}

function LangToggle({ lang, setLang }: { lang: Lang; setLang: (l: Lang) => void }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
      {(['en', 'es'] as Lang[]).map(l => (
        <button key={l} type="button" onClick={() => setLang(l)} aria-pressed={lang === l}
          className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
            lang === l ? 'bg-primary text-white' : 'text-slate-500 hover:text-slate-800'}`}>
          {l === 'en' ? 'English' : 'Español'}
        </button>
      ))}
    </div>
  );
}

function FactColumn({ title, items, icon: Icon, tone }: {
  title: string; items: string[]; icon: typeof Check; tone: 'yes' | 'no' | 'later';
}) {
  const color = tone === 'yes' ? 'text-action-500' : tone === 'no' ? 'text-slate-400' : 'text-primary';
  return (
    <div className={`rounded-lg p-4 ${tone === 'yes' ? 'bg-brand-50' : 'bg-slate-50'}`}>
      <h3 className="text-sm font-bold text-slate-800 mb-3">{title}</h3>
      <ul className="space-y-2.5">
        {items.map(i => (
          <li key={i} className="flex gap-2.5 text-sm text-slate-700 leading-relaxed">
            <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${color}`} aria-hidden />
            <span>{i}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Tab ──────────────────────────────────────────────────────────────────────

export function PlaybookTab() {
  const [lang, setLang] = useState<Lang>('en');
  const [open, setOpen] = useState<number | null>(0);
  const [query, setQuery] = useState('');
  const answers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ANSWERS.map((item, i) => ({ ...item, i })).filter(item =>
      !q || [item.q, item.who, item.a.en, item.a.es].some(x => x.toLowerCase().includes(q)));
  }, [query]);

  return (
    <div className="space-y-6">
      {/* Intro */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Partner playbook</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            What Wildy is, what's true today, and how we answer operators. Use it for every reply;
            the public page says the same things to operators.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <LangToggle lang={lang} setLang={setLang} />
          <a href="/for-business" target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-primary px-3 py-1.5 text-xs font-semibold text-primary hover:bg-brand-50 transition-colors">
            Public page <ExternalLink className="w-3.5 h-3.5" />
          </a>
          <CopyButton text={PUBLIC_PAGE} label="Copy link" />
        </div>
      </div>

      {/* Pitch */}
      <Card title="The pitch in one line">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 rounded-lg bg-brand-50 border-l-4 border-primary px-4 py-3">
          <p className="text-slate-800 leading-relaxed">{PITCH[lang]}</p>
          <CopyButton text={PITCH[lang]} />
        </div>
      </Card>

      {/* Facts */}
      <Card title="What's true today" subtitle="Check every reply against this. If it's not in the first column, it's not a promise.">
        <div className="grid gap-4 md:grid-cols-3">
          <FactColumn title="Today" items={TODAY} icon={Check} tone="yes" />
          <FactColumn title="Not yet" items={NOT_YET} icon={Minus} tone="no" />
          <FactColumn title="The plan, later" items={LATER} icon={Telescope} tone="later" />
        </div>
      </Card>

      {/* Answers */}
      <Card title="Answers to what operators ask" subtitle="Ready to paste. Adapt the greeting and keep the rest.">
        <label className="relative mb-3 block">
          <span className="sr-only">Search answers</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" aria-hidden />
          <input type="search" value={query} onChange={e => setQuery(e.target.value)}
            placeholder="Search: commission, Bókun, call, NGO…"
            className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm placeholder:text-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </label>
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {answers.length === 0 && (
            <p className="px-4 py-6 text-sm text-slate-500 text-center">
              No ready answer for that. Check it against "What's true today" and write it with the reply rules.
            </p>
          )}
          {answers.map(item => {
            const isOpen = open === item.i || (query.trim() !== '' && answers.length === 1);
            return (
              <div key={item.q}>
                <button type="button" onClick={() => setOpen(open === item.i ? null : item.i)}
                  aria-expanded={isOpen} aria-controls={`answer-${item.i}`}
                  className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left hover:bg-slate-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring">
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-slate-800">{item.q}</span>
                    <span className="block text-xs text-slate-500 mt-0.5">Asked by {item.who}</span>
                  </span>
                  <ChevronDown className={`w-4 h-4 shrink-0 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden />
                </button>
                {isOpen && (
                  <div id={`answer-${item.i}`} className="px-4 pb-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                    <p className="text-sm text-slate-700 leading-relaxed">{item.a[lang]}</p>
                    <CopyButton text={item.a[lang]} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Rules */}
        <Card title="Reply rules">
          <ol className="space-y-3">
            {RULES.map((r, i) => (
              <li key={r} className="flex gap-3 text-sm text-slate-700 leading-relaxed">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-bold text-primary">{i + 1}</span>
                <span>{r}</span>
              </li>
            ))}
          </ol>
        </Card>

        {/* Flow */}
        <Card title="From yes to live">
          <ol className="space-y-3">
            {FLOW.map((f, i) => (
              <li key={f.step} className="flex gap-3 text-sm">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-bold text-primary">{i + 1}</span>
                <span className="min-w-0">
                  <span className="font-semibold text-slate-800">{f.step}</span>
                  <span className="block text-slate-500 font-mono text-xs mt-0.5 break-words">{f.how}</span>
                </span>
              </li>
            ))}
          </ol>
        </Card>
      </div>

      {/* Live email */}
      <Card title={'"You\'re live" email'} subtitle="Send once the page is live. Replace [name] and [link].">
        <div className="flex flex-col-reverse sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 rounded-lg bg-slate-50 px-4 py-3">
          <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">{LIVE_EMAIL[lang]}</pre>
          <CopyButton text={LIVE_EMAIL[lang]} />
        </div>
      </Card>
    </div>
  );
}
